import React, { useMemo, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import type { ProductFiltersState } from '../../types/product-filters.types';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import { PiFunnelX } from 'react-icons/pi';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';

/** Debounce delay in milliseconds for search input to avoid overloading the server */
const SEARCH_DEBOUNCE_MS = 300;

interface ProductFiltersProps {
  filters: ProductFiltersState;
  onChange: (filters: ProductFiltersState) => void;
  onClear: () => void;
}

export const ProductFilters: React.FC<ProductFiltersProps> = ({
  filters,
  onChange,
  onClear,
}) => {
  const { t } = useI18n();
  const [localSearchValue, setLocalSearchValue] = useState(
    filters.search ?? ''
  );
  const debouncedSearchValue = useDebounce(
    localSearchValue,
    SEARCH_DEBOUNCE_MS
  );

  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);
  };

  // Update parent filters when debounced value changes
  React.useEffect(() => {
    onChange({ ...filters, search: debouncedSearchValue });
  }, [debouncedSearchValue, filters, onChange]);

  const currencyOptions: TagDropdownOption[] = useMemo(
    () => [
      {
        id: 'all',
        content: t('catalog.products.filters.currencyAll'),
        selected: filters.currency === 'all',
      },
      {
        id: 'CLP',
        content: 'CLP',
        selected: filters.currency === 'CLP',
      },
      {
        id: 'UF',
        content: 'UF',
        selected: filters.currency === 'UF',
      },
    ],
    [filters.currency, t]
  );

  const selectedCurrencyLabel =
    currencyOptions.find((option) => option.id === filters.currency)?.content ??
    t('catalog.products.filters.currencyAll');

  const handleCurrencyChange = (option: TagDropdownOption) => {
    onChange({
      ...filters,
      currency: option.id as ProductFiltersState['currency'],
    });
  };

  const hasFilters =
    (filters.search ?? '').trim().length > 0 || filters.currency !== 'all';

  return (
    <PageFiltersBar>
      <div className="flex flex-col gap-1 min-w-[260px] flex-1">
        <LabelTextInput
          label={t('catalog.products.filters.searchLabel')}
          size="md"
          labelPosition="top"
          inputProps={{
            placeholder: t('catalog.products.filters.searchPlaceholder'),
            value: localSearchValue,
            onChange: (event) => handleSearchChange(event.target.value),
          }}
        />
      </div>

      <TagDropdown
        content={selectedCurrencyLabel}
        options={currencyOptions}
        onOptionSelect={handleCurrencyChange}
        appearance="outline"
      />

      <Button
        variant="primary"
        appearance="soft"
        size="sm"
        onClick={onClear}
        disabled={!hasFilters}
        className="flex items-center gap-2"
      >
        <PiFunnelX className="w-4 h-4" />
        <Text size="small">{t('catalog.products.filters.clear')}</Text>
      </Button>
    </PageFiltersBar>
  );
};
